import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  Button,
  StyleSheet,
  TouchableOpacity,
  Modal,
  FlatList,
  Alert,
  TextInput,
  ActivityIndicator,
  Linking,
  Image,
  AppState,
  Platform
} from 'react-native';

import {
  onSnapshot,
  collection,
  query,
  where,
  updateDoc,
  doc,
  getDoc,
  getDocs,
  addDoc,
  deleteDoc,
  setDoc,
  runTransaction
} from 'firebase/firestore';

import { db, auth } from '../firebaseConfig';
import { MaterialCommunityIcons, MaterialIcons, Ionicons } from '@expo/vector-icons';
import * as Notifications from 'expo-notifications';
import { signOut } from 'firebase/auth';
import { TabView, TabBar } from 'react-native-tab-view';
import LottieView from 'lottie-react-native';
import MotoboySlideMenu from './MotoboySlideMenu';
import 'react-native-gesture-handler';

const confirmToggle = async (message) => {
  if (Platform.OS === 'web') {
    return window.confirm(message);
  }
  return new Promise((resolve) => {
    Alert.alert(
      'Alterar Status',
      message,
      [
        { text: 'Não', style: 'cancel', onPress: () => resolve(false) },
        { text: 'Sim', onPress: () => resolve(true) },
      ],
      { cancelable: true }
    );
  });
};

const ensureMotoboyDocExists = async () => {
  const user = auth.currentUser;
  if (!user) return;
  const ref = doc(db, 'usuarios', user.uid);
  const snap = await getDoc(ref);
  if (!snap.exists() || typeof snap.data().isOnline !== 'boolean') {
    await setDoc(ref, { isOnline: false }, { merge: true });
  }
};

const MotoboyHomeScreen = ({ navigation }) => {
  const [view, setView] = useState('acceptOrders');
  const [menuVisible, setMenuVisible] = useState(false);
  const [orders, setOrders] = useState([]);
  const [unreadMessagesMap, setUnreadMessagesMap] = useState({});
  const [notificationCount, setNotificationCount] = useState(0);
  const [verificationCode, setVerificationCode] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedPedidoIndex, setSelectedPedidoIndex] = useState(null);
  const [verificationModalVisible, setVerificationModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [motoboyName, setMotoboyName] = useState('');
  const [motoboyCPF, setMotoboyCPF] = useState('');
  const [motoboyPhone, setMotoboyPhone] = useState('');
  const [motoboyPlate, setMotoboyPlate] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [index, setIndex] = useState(0);
  const [isSearching, setIsSearching] = useState(false);
  const [waitingOrders, setWaitingOrders] = useState([]);
  const [isDemandVisible, setIsDemandVisible] = useState(false);
  const lottieRef = useRef(null);
  const [isOnline, setIsOnline] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [motoboyId, setMotoboyId] = useState(null);
  const [companyId, setCompanyId] = useState(null);
  const [pendingOrders, setPendingOrders] = useState([]);
  const [acceptedOrders, setAcceptedOrders] = useState([]);
  const [completedOrders, setCompletedOrders] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);

  // ---- Captcha States ----
  const [captchaVisible, setCaptchaVisible] = useState(false);
  const [captchaDigit, setCaptchaDigit] = useState(null); // 0..9
  const [captchaInput, setCaptchaInput] = useState('');
  const [captchaOrderId, setCaptchaOrderId] = useState(null);
  const [captchaExpiresAt, setCaptchaExpiresAt] = useState(null);

  const [routesConfig] = useState([{ key: 'orders', title: 'Pedidos' }]);

  Notifications.setNotificationHandler({
    handleNotification: async () => ({
      shouldShowAlert: true,
      shouldPlaySound: true,
      shouldSetBadge: true,
    }),
  });

  useEffect(() => {
    navigation.setOptions({ headerShown: false });
    const unsubscribePedidos = loadPedidos();
    return () => { if (unsubscribePedidos) unsubscribePedidos(); };
  }, [view, navigation]);

  useEffect(() => {
    if (view === 'acceptOrders') {
      setIsSearching(true);
      const unsubscribe = loadPedidos();
      return () => {
        setIsSearching(false);
        if (unsubscribe) unsubscribe();
      };
    } else {
      setIsSearching(false);
    }
  }, [view]);

  useEffect(() => {
    const initializeApp = async () => {
      try {
        setInitialLoading(true);
        const user = auth.currentUser;
        if (!user) {
          Alert.alert("Erro", "Usuário não autenticado.");
          return;
        }
        await Promise.all([
          ensureMotoboyDocExists(),
          loadMotoboyData(),
          reloadOrders(),
          new Promise(resolve => setTimeout(resolve, 1000)),
        ]);
      } catch (error) {
        console.error('Erro ao carregar dados iniciais:', error);
      } finally {
        setInitialLoading(false);
      }
    };
    initializeApp();
  }, []);

  useEffect(() => {
    if (view === 'profile') loadMotoboyData();
  }, [view]);

  // só controla a animação
  useEffect(() => {
    setIsSearching(!!isOnline);
  }, [isOnline]);

  useEffect(() => {
    if (isOnline) {
      const unsubscribePedidos = loadPedidos();
      return () => { if (unsubscribePedidos) unsubscribePedidos(); };
    }
  }, [isOnline]);

  useEffect(() => {
    Notifications.requestPermissionsAsync().then((status) => {
      if (!status.granted) {
        Alert.alert('Permissões Necessárias', 'Ative as notificações para receber atualizações.');
      }
    });
  }, []);

  useEffect(() => {
    if (!orderId || !motoboyId || !companyId) return;
    const chatRef = doc(db, 'chats', orderId);

    const init = async () => {
      try {
        const snapshot = await getDoc(chatRef);
        if (!snapshot.exists()) {
          await setDoc(chatRef, {
            messages: [],
            orderId,
            participants: [motoboyId, companyId],
          });
        }
      } catch (error) {
        console.error('Erro ao inicializar o chat:', error);
      }
    };

    init();
    const unsubscribe = onSnapshot(chatRef, () => {});
    return () => unsubscribe();
  }, [orderId, motoboyId, companyId]);

  // já está em 'pedidos' aqui
  useEffect(() => {
    const user = auth.currentUser;
    const unsubscribe = onSnapshot(
      collection(db, 'pedidos'),
      (snapshot) => {
        snapshot.docChanges().forEach((change) => {
          if (change.type === 'modified') {
            const data = change.doc.data();
            if (user && data.motoboyId === user.uid) {
              if (data.status === 'cancelled') {
                triggerNotification('Pedido Cancelado', `O pedido "${data.description}" foi cancelado pela empresa.`);
              }
              if (data.status === 'accepted') {
                triggerNotification('Pedido Alterado', `O pedido "${data.description}" foi alterado pela empresa.`);
              }
            }
          }
        });
      },
      (error) => console.error('Erro ao monitorar alterações:', error)
    );
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) {
      console.error('Motoboy não autenticado.');
      return;
    }
    const pedidosQuery = query(collection(db, 'pedidos'), where('motoboyId', '==', user.uid));
    const unsubscribe = onSnapshot(
      pedidosQuery,
      (snapshot) => {
        setOrders((prevOrders) => {
          let updatedOrders = [...prevOrders];
          snapshot.docChanges().forEach((change) => {
            const data = change.doc.data();
            const orderId = change.doc.id;
            if (change.type === 'modified') {
              updatedOrders = updatedOrders.map((order) =>
                order.id === orderId ? { ...order, ...data } : order
              );
              if (data.motoboyId === user.uid) {
                if (data.status === 'cancelled') {
                  triggerNotification(data.description, 'Pedido Cancelado');
                } else if (data.status === 'accepted') {
                  triggerNotification(data.description, 'Pedido Alterado');
                }
              }
            } else if (change.type === 'removed') {
              updatedOrders = updatedOrders.filter((order) => order.id !== orderId);
            }
          });
          return updatedOrders;
        });
      },
      (error) => { console.error('Erro ao monitorar pedidos:', error); }
    );
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) {
      console.error("Motoboy não autenticado.");
      return;
    }
    if (view === 'accepted') {
      const intervalId = setInterval(async () => {
        try {
          const acceptedQueryRef = query(
            collection(db, 'pedidos'),
            where('status', '==', 'accepted'),
            where('motoboyId', '==', user.uid)
          );
          const snapshot = await getDocs(acceptedQueryRef);
          const acceptedOrders = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
          setOrders((prevOrders) => {
            const currentAccepted = prevOrders.filter((order) => order.status === 'accepted');
            const missingAccepted = acceptedOrders.filter(
              (order) => !currentAccepted.some((o) => o.id === order.id)
            );
            return [...prevOrders, ...missingAccepted];
          });
        } catch (error) {
          console.error("Erro ao verificar pedidos aceitos:", error);
        }
      }, 2000);
      return () => clearInterval(intervalId);
    }
  }, [view]);

  useEffect(() => {
    const unsubscribePedidos = loadPedidos();

    const waitingOrdersQuery = query(
      collection(db, 'pedidos'),
      where('status', '==', 'created')
    );
    const unsubscribeWaitingOrders = onSnapshot(waitingOrdersQuery, (snapshot) => {
      const waitingArray = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        formattedOrderValue: new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL',
        }).format(Number(doc.data().orderValue) || 0),
      }));
      setWaitingOrders(waitingArray);
    });

    return () => {
      if (unsubscribePedidos) unsubscribePedidos();
      if (unsubscribeWaitingOrders) unsubscribeWaitingOrders();
    };
  }, [view]);

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;

    const pendingQueryRef = query(collection(db, "pedidos"), where("status", "==", "pending"));
    const acceptedQueryRef = query(collection(db, "pedidos"), where("motoboyId", "==", user.uid), where("status", "==", "accepted"));
    const completedQueryRef = query(collection(db, "pedidos"), where("motoboyId", "==", user.uid), where("status", "==", "completed"));

    const unsubscribePending = onSnapshot(pendingQueryRef, (snapshot) => {
      const pending = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setPendingOrders(pending);
    });
    const unsubscribeAccepted = onSnapshot(acceptedQueryRef, (snapshot) => {
      const accepted = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setAcceptedOrders(accepted);
    });
    const unsubscribeCompleted = onSnapshot(completedQueryRef, (snapshot) => {
      const completed = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setCompletedOrders(completed);
    });

    return () => {
      unsubscribePending();
      unsubscribeAccepted();
      unsubscribeCompleted();
    };
  }, []);

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;
    const motoboyDocRef = doc(db, 'usuarios', user.uid);

    const unsubscribe = onSnapshot(motoboyDocRef, (docSnapshot) => {
      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        setIsOnline(data.isOnline || false);
        console.log(`📡 Status atualizado via Firestore: ${data.isOnline ? 'Online' : 'Offline'}`);
      }
    });

    return () => unsubscribe();
  }, []);

  const handleAcceptRoute = async (routeId) => {
    try {
      const user = auth.currentUser;
      if (!user) {
        Alert.alert('Erro', 'Usuário não autenticado.');
        return;
      }

      const acceptedOrdersQueryRef = query(collection(db, 'pedidos'), where('motoboyId', '==', user.uid), where('status', '==', 'accepted'));
      const acceptedRoutesQuery = query(collection(db, 'routes'), where('motoboyId', '==', user.uid), where('status', '==', 'accepted'));

      const [acceptedOrdersSnapshot, acceptedRoutesSnapshot] = await Promise.all([
        getDocs(acceptedOrdersQueryRef),
        getDocs(acceptedRoutesQuery),
      ]);

      if (!acceptedOrdersSnapshot.empty || !acceptedRoutesSnapshot.empty) {
        Alert.alert('Atenção', 'Você já aceitou um pedido ou uma rota. Finalize antes de aceitar outro.', [{ text: 'OK' }]);
        return;
      }

      const routeRef = doc(db, 'routes', routeId);
      await updateDoc(routeRef, { status: 'accepted', motoboyId: user.uid });

      Alert.alert('Sucesso', 'Rota aceita com sucesso!');
      reloadOrders();
    } catch {
      Alert.alert('Erro', 'Não foi possível aceitar a rota.');
    }
  };

  const reloadOrders = async () => {
    const user = auth.currentUser;
    if (!user) {
      console.error('Motoboy não autenticado.');
      return;
    }
    try {
      const pendingQueryRef = query(collection(db, 'pedidos'), where('status', '==', 'pending'));
      const acceptedQueryRef = query(collection(db, 'pedidos'), where('motoboyId', '==', user.uid), where('status', '==', 'accepted'));
      const completedQueryRef = query(collection(db, 'pedidos'), where('motoboyId', '==', user.uid), where('status', '==', 'completed'));

      const [pendingSnapshot, acceptedSnapshot, completedSnapshot] = await Promise.all([
        getDocs(pendingQueryRef), getDocs(acceptedQueryRef), getDocs(completedQueryRef),
      ]);

      setPendingOrders(pendingSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
      setAcceptedOrders(acceptedSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
      setCompletedOrders(completedSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
    } catch (error) {
      console.error('Erro ao carregar os pedidos:', error);
    }
  };

  useEffect(() => { reloadOrders(); }, [view]);

  const loadPedidos = () => {
    const user = auth.currentUser;
    const status = view === 'acceptOrders' ? 'pending' : view === 'accepted' ? 'accepted' : 'completed';

    let pedidoQuery;
    if (view === 'acceptOrders') {
      pedidoQuery = query(collection(db, 'pedidos'), where('status', '==', status));
    } else {
      pedidoQuery = query(collection(db, 'pedidos'), where('status', '==', status), where('motoboyId', '==', user?.uid || ''));
    }

    const unsubscribePedidos = onSnapshot(pedidoQuery, (snapshot) => {
      const pedidosArray = snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          motoboyPhone: data.motoboyPhone || 'Número não disponível',
          changeValue: data.changeValue || null,
          locationType: data.locationType || data.location?.type || 'N/A',
          locationValue: data.locationValue || data.location?.value || 'N/A',
          orderValue: Number(data.orderValue) || 0,
          formattedOrderValue: new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
          }).format(Number(data.orderValue) || 0),
          createdAt: data.createdAt || null,
        };
      });

      setOrders((prevOrders) => {
        const hasAcceptedOrder = prevOrders.some((order) => order.status === 'accepted');
        if (hasAcceptedOrder && view === 'acceptOrders') return [];
        if (view === 'acceptOrders') {
          return pedidosArray.filter((pedido) => pedido.status === 'pending');
        } else if (view === 'accepted') {
          return pedidosArray.filter((pedido) => pedido.status === 'accepted' && pedido.motoboyId === user?.uid);
        } else if (view === 'completed') {
          return pedidosArray.filter((pedido) => pedido.status === 'completed' && pedido.motoboyId === user?.uid);
        }
        return pedidosArray;
      });

      if (view === 'acceptOrders' && pedidosArray.length > 0 && isOnline) {
        triggerNotification(pedidosArray[0].description, 'Novo Pedido Disponível!');
      }
    });

    const cancelledQueryRef = query(
      collection(db, 'pedidos'),
      where('motoboyId', '==', user?.uid || ''),
      where('status', '==', 'cancelled')
    );

    const unsubscribeCancelled = onSnapshot(cancelledQueryRef, (snapshot) => {
      if (!snapshot.empty) {
        snapshot.docs.forEach((doc) => {
          const data = doc.data();
          Alert.alert('Aviso de Cancelamento', `O pedido "${data.description}" foi cancelado pela empresa.`);
          Notifications.scheduleNotificationAsync({
            content: { title: 'Pedido Cancelado', body: `O pedido "${data.description}" foi cancelado pela empresa.`, sound: true },
            trigger: null,
          });
        });
        setOrders((prev) => prev.filter((order) => order.status !== 'cancelled'));
      }
    });

    return () => {
      unsubscribePedidos();
      unsubscribeCancelled();
    };
  };

  const triggerNotification = async (title, description, status) => {
    let body = '';
    switch (status) {
      case 'cancelled':
        body = `O pedido "${description}" foi cancelado pela empresa.`;
        break;
      case 'updated':
        body = `O pedido "${description}" foi alterado pela empresa. Verifique os novos detalhes.`;
        break;
      default:
        body = `Novo Pedido Velox: ${description}`;
    }

    await Notifications.scheduleNotificationAsync({
      content: { title, body, sound: true, badge: 1 },
      trigger: null,
    });
  };

  const handleOpenChat = (item, navigation) => {
    navigation.navigate('Chat', { orderId: item.id, companyId: item.companyId, motoboyId: auth.currentUser.uid });
  };

  const renderScene = ({ route }) => {
    switch (route.key) {
      case 'orders':
        return renderOrders();
      default:
        return null;
    }
  };

  // ====== CAPTCHAS / ACEITAR COM TRANSAÇÃO ======

  const startCaptcha = (orderId) => {
    const digit = Math.floor(Math.random() * 10);
    setCaptchaDigit(digit);
    setCaptchaInput('');
    setCaptchaOrderId(orderId);
    setCaptchaExpiresAt(new Date(Date.now() + 15 * 1000)); // 15s
    setCaptchaVisible(true);
  };

  const cancelCaptcha = () => {
    setCaptchaVisible(false);
    setCaptchaDigit(null);
    setCaptchaInput('');
    setCaptchaOrderId(null);
    setCaptchaExpiresAt(null);
  };

  useEffect(() => {
    if (!captchaVisible || !captchaExpiresAt) return;
    const t = setInterval(() => {
      if (new Date() > captchaExpiresAt) {
        const digit = Math.floor(Math.random() * 10);
        setCaptchaDigit(digit);
        setCaptchaInput('');
        setCaptchaExpiresAt(new Date(Date.now() + 15 * 1000));
      }
    }, 500);
    return () => clearInterval(t);
  }, [captchaVisible, captchaExpiresAt]);

  const acceptOrderWithTransaction = async (orderId) => {
    const user = auth.currentUser;
    if (!user) {
      Alert.alert('Erro', 'Usuário não autenticado.');
      return { ok: false };
    }

    // Checagem rápida: já tem pedido/rota aceito?
    const [acceptedOrdersSnapshot, acceptedRoutesSnapshot] = await Promise.all([
      getDocs(query(collection(db, 'pedidos'), where('motoboyId', '==', user.uid), where('status', '==', 'accepted'))),
      getDocs(query(collection(db, 'routes'), where('motoboyId', '==', user.uid), where('status', '==', 'accepted'))),
    ]);

    if (!acceptedOrdersSnapshot.empty || !acceptedRoutesSnapshot.empty) {
      Alert.alert('Atenção', 'Você já aceitou um pedido ou uma rota. Finalize antes de aceitar outro.');
      return { ok: false, code: 'HAS_ACTIVE' };
    }

    const orderRef = doc(db, 'pedidos', orderId);
    const motoboyRef = doc(db, 'usuarios', user.uid);

    try {
      const res = await runTransaction(db, async (tx) => {
        const [orderSnap, motoSnap] = await Promise.all([tx.get(orderRef), tx.get(motoboyRef)]);
        if (!orderSnap.exists()) {
          return { ok: false, code: 'NOT_FOUND' };
        }

        const order = orderSnap.data() || {};
        if (order.status !== 'pending' || !!order.motoboyId) {
          return { ok: false, code: 'ALREADY_TAKEN' };
        }

        const u = motoSnap.exists() ? motoSnap.data() : {};
        tx.update(orderRef, {
          status: 'accepted',
          motoboyId: user.uid,
          motoboyName: u.motoboyName || 'Nome não disponível',
          motoboyPlate: u.motoboyPlate || 'Placa não disponível',
          motoboyPhone: u.motoboyPhone || 'Número não disponível',
          acceptedAt: new Date(),
        });

        return { ok: true };
      });

      return res;
    } catch (err) {
      console.error('acceptOrderWithTransaction error', err);
      return { ok: false, code: 'INTERNAL' };
    }
  };

  const submitCaptchaAndAccept = async () => {
    try {
      if (captchaDigit === null || captchaOrderId === null) return;

      if (String(captchaInput).trim() !== String(captchaDigit)) {
        Alert.alert('Código incorreto', 'Digite o número exibido para confirmar.');
        return;
      }
      if (new Date() > captchaExpiresAt) {
        Alert.alert('Expirou', 'O código expirou. Foi gerado um novo.');
        startCaptcha(captchaOrderId);
        return;
      }

      const res = await acceptOrderWithTransaction(captchaOrderId);

      if (res?.ok) {
        cancelCaptcha();
        Alert.alert('Sucesso', 'Pedido aceito com sucesso!');
        setView('accepted');
        return;
      }

      if (res?.code === 'ALREADY_TAKEN') {
        cancelCaptcha();
        Alert.alert('Ops', 'Outro motoboy já aceitou este pedido.');
        await reloadOrders();
        return;
      }
      if (res?.code === 'HAS_ACTIVE') {
        return;
      }
      if (res?.code === 'NOT_FOUND') {
        cancelCaptcha();
        Alert.alert('Indisponível', 'Pedido não encontrado.');
        await reloadOrders();
        return;
      }

      Alert.alert('Erro', 'Não foi possível aceitar o pedido.');
    } catch (err) {
      Alert.alert('Erro', err?.message || 'Falha ao aceitar.');
    }
  };

  // ====== FIM CAPTCHA ======

  const handleOpenVerificationModal = (order, pedidoIndex, isRouteOrder) => {
    setVerificationCode('');
    setSelectedOrder({ ...order, isRouteOrder });
    setSelectedPedidoIndex(pedidoIndex);
    setVerificationModalVisible(true);
  };

  const handleVerifyCode = async (isRouteOrder) => {
    try {
      const collectionName = isRouteOrder ? 'routes' : 'pedidos';
      const orderRef = doc(db, collectionName, selectedOrder.id);
      const orderDoc = await getDoc(orderRef);

      if (orderDoc.exists()) {
        const orderData = orderDoc.data();

        if (isRouteOrder) {
          const Pedidos = orderData.orders[selectedPedidoIndex];
          if (String(Pedidos.verificationCode) === String(verificationCode)) {
            const completionTime = new Date();

            const updatedOrders = orderData.orders.map((order, index) => {
              if (index === selectedPedidoIndex) {
                return { ...order, status: 'completed', completedAt: completionTime };
              }
              return order;
            });

            const allCompleted = updatedOrders.every((order) => order.status === 'completed');

            await updateDoc(orderRef, {
              orders: updatedOrders,
              status: allCompleted ? 'completed' : orderData.status,
              completedAt: allCompleted ? completionTime : null,
            });

            if (allCompleted) {
              await saveRouteToHistory(orderData, updatedOrders);
              Alert.alert('Sucesso', 'Todos os pedidos da rota foram concluídos e movidos para o histórico!');
            } else {
              Alert.alert('Sucesso', 'Pedido de rota verificado com sucesso!');
            }

            setVerificationModalVisible(false);
          } else {
            Alert.alert('Erro', 'Chave de verificação inválida');
          }
        } else {
          if (String(orderData.verificationCode) === String(verificationCode)) {
            await updateDoc(orderRef, { status: 'completed', completedAt: new Date() });
            Alert.alert('Sucesso', 'Pedido verificado com sucesso!');
            setVerificationModalVisible(false);
          } else {
            Alert.alert('Erro', 'Chave de verificação inválida');
          }
        }
      } else {
        Alert.alert('Erro', 'Pedido não encontrado');
      }
    } catch (error) {
      Alert.alert('Erro ao verificar o código: ' + error.message);
    }
  };

  const handleCancelConfirmation = (orderId, isRouteOrder = false) => {
    Alert.alert('Confirmação', 'Tem certeza que deseja cancelar o pedido?', [
      { text: 'Não', style: 'cancel' },
      { text: 'Sim', onPress: () => handleCancelOrder(orderId, isRouteOrder) },
    ]);
  };

  const handleCancelOrder = async (orderId, isRouteOrder = false) => {
    try {
      const collectionName = isRouteOrder ? 'routes' : 'pedidos';
      const orderRef = doc(db, collectionName, orderId);

      await updateDoc(orderRef, {
        status: 'pending',
        motoboyId: '',
        motoboyName: '',
        motoboyPlate: '',
      });

      Alert.alert('Sucesso', 'Pedido cancelado com sucesso! Ele voltou para os pedidos pendentes.');
    } catch {
      Alert.alert('Erro', 'Não foi possível cancelar o pedido.');
    }
  };

  const loadMotoboyData = async () => {
    setLoading(true);
    try {
      const user = auth.currentUser;
      const motoboyDocRef = doc(db, 'usuarios', user.uid);
      const motoboyDoc = await getDoc(motoboyDocRef);

      if (motoboyDoc.exists()) {
        const motoboyData = motoboyDoc.data();
        setMotoboyName(motoboyData.motoboyName || '');
        setMotoboyCPF(motoboyData.motoboyCPF || '');
        setMotoboyPhone(motoboyData.motoboyPhone || '');
        setMotoboyPlate(motoboyData.motoboyPlate || '');
      } else {
        Alert.alert('Erro', 'Não encontramos dados para este perfil.');
      }
    } catch {
      Alert.alert('Erro', 'Não foi possível carregar os dados.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!motoboyName.trim() || !motoboyPhone.trim() || !motoboyPlate.trim()) {
      Alert.alert('Erro', 'Os campos Nome, Telefone e Placa são obrigatórios. Por favor, preencha todos os campos.');
      return;
    }

    setLoading(true);
    try {
      const user = auth.currentUser;
      const motoboyDocRef = doc(db, 'usuarios', user.uid);

      await updateDoc(motoboyDocRef, {
        motoboyName: motoboyName.trim(),
        motoboyPhone: motoboyPhone.trim(),
        motoboyPlate: motoboyPlate.trim(),
      });

      Alert.alert('Sucesso', 'Perfil do motoboy atualizado com sucesso!');
      setIsEditing(false);
      loadMotoboyData();
    } catch {
      Alert.alert('Erro', 'Não foi possível salvar os dados do motoboy.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoff = async () => {
    try {
      await signOut(auth);
      Alert.alert('Sucesso', 'Você foi desconectado.');
      navigation.navigate('LoginScreen');
    } catch {
      Alert.alert('Erro', 'Não foi possível desconectar.');
    }
  };

  const handleSupportClick = () => {
    const phoneNumber = '5561995998395';
    const message = 'Olá, gostaria de suporte !';
    const url = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    Linking.openURL(url).catch(() => {
      alert('Não foi possível abrir o WhatsApp.');
    });
  };

  const ChatScreen = ({ route }) => {
    const { orderId, companyId, motoboyId } = route.params;
    const [messages, setMessages] = useState([]);

    useEffect(() => {
      const chatRef = doc(db, 'chats', orderId);
      const unsubscribe = onSnapshot(chatRef, async (snapshot) => {
        if (!snapshot.exists()) return;
        const data = snapshot.data() || {};
        const newMessages = data.messages || [];
        setMessages(newMessages);

        const currentUserId = auth.currentUser?.uid;
        let changed = false;
        const updated = newMessages.map((msg) => {
          if (msg.senderId !== currentUserId && (!msg.readBy || !msg.readBy.includes(currentUserId))) {
            changed = true;
            return { ...msg, readBy: msg.readBy ? [...msg.readBy, currentUserId] : [currentUserId] };
          }
          return msg;
        });
        if (changed) {
          await updateDoc(chatRef, { messages: updated });
        }
      });
      return () => unsubscribe();
    }, [orderId]);

    return (
      <View>
        <Text>Chat para Pedidos {orderId}</Text>
      </View>
    );
  };

  const renderOrders = () => {
    let displayedOrders = [];
    if (view === 'acceptOrders') displayedOrders = pendingOrders;
    else if (view === 'accepted') displayedOrders = acceptedOrders;
    else if (view === 'completed') displayedOrders = completedOrders;

    return (
      <View style={styles.ordersContainer}>
        {view === 'acceptOrders' && !isOnline ? (
          <View style={styles.offlineContainer}>
            <Text style={styles.offlineText}>Você está offline. Fique online para visualizar pedidos.</Text>
          </View>
        ) : displayedOrders.length === 0 && !displayedOrders.some((order) => order.status === 'accepted') ? (
          null
        ) : (
          <FlatList
            data={displayedOrders}
            keyExtractor={(item) => item.id}
            renderItem={({ item }) => (
              <OrderItem
                item={item}
                navigation={navigation}
                onAccept={() => startCaptcha(item.id)}
                onCancel={handleCancelConfirmation}
                onVerify={(item) => handleOpenVerificationModal(item, null, false)}
                onChat={(item) => handleOpenChat(item, navigation)}
                view={view}
                unreadMessagesMap={unreadMessagesMap}
              />
            )}
          />
        )}

        {/* Botão Online/Offline */}
        <TouchableOpacity
          style={[
            styles.statusButton,
            {
              shadowColor: isOnline ? '#28a745' : '#dc3545',
              borderColor: isOnline ? '#28a745' : '#dc3545',
              backgroundColor: isOnline ? '#28a745' : '#dc3545',
            },
          ]}
          onPress={handleToggleOnline}
        >
          <Text style={styles.statusButtonText}>{isOnline ? 'ON' : 'OFF'}</Text>
        </TouchableOpacity>
      </View>
    );
  };

  useEffect(() => {
    const unsubscribes = [];
    const user = auth.currentUser;
    if (!user) return;

    const acceptedOrdersLocal = orders.filter((o) => o.status === 'accepted');
    acceptedOrdersLocal.forEach((order) => {
      const chatRef = doc(db, 'chats', order.id);
      const unsubscribe = onSnapshot(chatRef, (snapshot) => {
        if (!snapshot.exists()) {
          setUnreadMessagesMap((prev) => ({ ...prev, [order.id]: 0 }));
          return;
        }
        const data = snapshot.data() || {};
        const messages = data.messages || [];
        const unreadCount = messages.filter(
          (msg) => msg.senderId !== user.uid && (!msg.readBy || !msg.readBy.includes(user.uid))
        ).length;

        setUnreadMessagesMap((prev) => ({ ...prev, [order.id]: unreadCount }));
      });
      unsubscribes.push(unsubscribe);
    });

    return () => { unsubscribes.forEach((unsub) => unsub()); };
  }, [orders]);

  const updateMotoboyStatus = async (status) => {
    const user = auth.currentUser;
    if (!user) {
      Alert.alert('Erro', 'Usuário não autenticado.');
      return;
    }
    const motoboyDocRef = doc(db, 'usuarios', user.uid);
    try {
      await setDoc(motoboyDocRef, { isOnline: status }, { merge: true });
      console.log(`🔥 Firestore isOnline => ${status}`);
    } catch (err) {
      console.error('Erro ao atualizar status:', err);
      Alert.alert('Erro', 'Não foi possível atualizar seu status.');
    }
  };

  const handleToggleOnline = async () => {
    try {
      const user = auth.currentUser;
      if (!user) {
        Alert.alert('Erro', 'Usuário não autenticado.');
        return;
      }

      const want = !(isOnline === true);
      const ok = await confirmToggle(`Deseja realmente ficar ${isOnline ? 'Offline' : 'Online'}?`);
      if (!ok) return;

      await updateMotoboyStatus(want); // onSnapshot refletirá no estado
      Alert.alert('Status Alterado', `Você agora está ${want ? 'Online' : 'Offline'}.`);
    } catch (error) {
      console.error('Erro ao processar alteração de status:', error);
      Alert.alert('Erro', 'Ocorreu um erro ao tentar alterar o status.');
    }
  };

  const checkAutoOffline = () => {
    const now = new Date();
    const currentHours = now.getHours();
    const currentMinutes = now.getMinutes();
    if (currentHours === 4 && currentMinutes === 0) {
      updateMotoboyStatus(false);
      Alert.alert('Desconectado', 'Você foi desconectado automaticamente às 4:00 da manhã.');
    }
  };

  if (initialLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#00FF9D" />
        <Text style={{ color: '#FFF', marginTop: 10 }}>Seja Bem Vindo !, Carregando...</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <MotoboySlideMenu navigation={navigation} setView={setView} />

      <View style={styles.header}>
        <Text style={styles.sectionTitle}>{renderTitle(view)}</Text>
        <TouchableOpacity
          style={styles.logoutButtonContainer}
          onPress={handleLogoff}
          hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
        >
          <MaterialIcons name="logout" size={24} color="#FFF" />
        </TouchableOpacity>
      </View>

      {isSearching && isOnline && orders.length === 0 && (
        <View style={styles.fixedAnimationContainer}>
          <LottieView
            ref={lottieRef}
            source={require('../../assets/animations/radar.json')}
            autoPlay
            loop
            style={styles.fixedAnimation}
          />
          <Text style={styles.searchingText}>Buscando pedidos...</Text>
        </View>
      )}

      {view === 'profile' ? (
        <View style={styles.profileContainer}>
          {loading ? (
            <ActivityIndicator size="large" color="#007AFF" />
          ) : (
            <>
              <View style={styles.inputContainer}>
                <MaterialIcons name="person" size={24} color="#FFF" />
                <TextInput
                  placeholder="Nome"
                  placeholderTextColor="#999"
                  value={motoboyName}
                  onChangeText={setMotoboyName}
                  style={styles.input}
                  editable={isEditing}
                />
              </View>

              <View style={styles.inputContainer}>
                <MaterialIcons name="credit-card" size={24} color="#FFF" />
                <TextInput
                  placeholder="CPF"
                  placeholderTextColor="#999"
                  value={motoboyCPF}
                  onChangeText={setMotoboyCPF}
                  style={styles.input}
                  editable={false}
                />
              </View>

              <View style={styles.inputContainer}>
                <MaterialIcons name="phone" size={24} color="#FFF" />
                <TextInput
                  placeholder="Telefone"
                  placeholderTextColor="#999"
                  value={motoboyPhone}
                  onChangeText={setMotoboyPhone}
                  keyboardType="phone-pad"
                  style={styles.input}
                  editable={isEditing}
                />
              </View>

              <View style={styles.inputContainer}>
                <MaterialIcons name="directions-bike" size={24} color="#FFF" />
                <TextInput
                  placeholder="Placa da Moto"
                  placeholderTextColor="#999"
                  value={motoboyPlate}
                  onChangeText={setMotoboyPlate}
                  style={styles.input}
                  editable={isEditing}
                />
              </View>

              {isEditing ? (
                <TouchableOpacity style={styles.saveButton} onPress={handleSaveProfile}>
                  <Text style={styles.saveButtonText}>Salvar</Text>
                </TouchableOpacity>
              ) : (
                <TouchableOpacity style={styles.editButton} onPress={() => setIsEditing(true)}>
                  <Text style={styles.editButtonText}>Editar Perfil</Text>
                </TouchableOpacity>
              )}
            </>
          )}
        </View>
      ) : (
        <TabView
          navigationState={{ index, routes: routesConfig }}
          renderScene={renderScene}
          onIndexChange={setIndex}
          renderTabBar={(props) => (
            <TabBar
              {...props}
              style={{ backgroundColor: '#203a43', marginTop: 100 }}
              indicatorStyle={{ backgroundColor: '#00FF9D', height: 4 }}
              activeColor="#FFFFFF"
              inactiveColor="#888888"
            />
          )}
        />
      )}

      <TouchableOpacity style={styles.supportButton} onPress={handleSupportClick}>
        <MaterialIcons name="headset-mic" size={30} color="#FFF" />
      </TouchableOpacity>

      <TouchableOpacity
        style={{ position: 'absolute', bottom: 20, left: 20 }}
        onPress={() => setIsDemandVisible(true)}
      >
        <View
          style={{
            width: 50,
            height: 50,
            backgroundColor: 'white',
            borderRadius: 50,
            justifyContent: 'center',
            alignItems: 'center',
            borderWidth: 3,
            borderColor: '#007AFF',
            shadowColor: '#000',
            shadowOpacity: 0.3,
            shadowRadius: 10,
            shadowOffset: { width: 0, height: 5 },
            elevation: 5,
          }}
        >
          <LottieView
            source={require('../../assets/animations/demand-icon.json')}
            autoPlay
            loop
            style={{ width: 70, height: 70, backgroundColor: 'transparent' }}
          />
        </View>
      </TouchableOpacity>

      <Modal
        visible={isDemandVisible}
        transparent={true}
        animationType="slide"
        onRequestClose={() => setIsDemandVisible(false)}
      >
        <View style={styles.modalContainer}>
          <View style={styles.demandModalContent}>
            <Text style={styles.demandTitle}>Pedidos em Espera</Text>

            {waitingOrders.length > 0 ? (
              <FlatList
                data={waitingOrders}
                keyExtractor={(item) => item.id}
                renderItem={({ item }) => (
                  <View style={styles.demandOrderCard}>
                    <Text style={styles.cardTitle}>{item.description}</Text>
                    <Text style={styles.cardText}>
                      <Text style={styles.cardLabel}>Empresa: </Text>
                      {item.companyName || 'Empresa não informada'}
                    </Text>
                    <Text style={styles.cardText}>
                      <Text style={styles.cardLabel}>Cliente: </Text>
                      {item.clientName}
                    </Text>
                    <Text style={styles.cardText}>
                      <Text style={styles.cardLabel}>Endereço: </Text>
                      {item.deliveryLocation}
                    </Text>
                    <Text style={styles.cardText}>
                      <Text style={styles.cardLabel}>Valor: </Text>
                      {item.formattedOrderValue}
                    </Text>
                  </View>
                )}
                contentContainerStyle={styles.listContentContainer}
                showsVerticalScrollIndicator={false}
              />
            ) : (
              <Text style={styles.noOrdersText}>Nenhum pedido em espera no momento.</Text>
            )}

            <TouchableOpacity style={styles.closeButton} onPress={() => setIsDemandVisible(false)}>
              <Text style={styles.closeButtonText}>Fechar</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      <Modal
        visible={verificationModalVisible}
        transparent={true}
        animationType="slide"
        onRequestClose={() => setVerificationModalVisible(false)}
      >
        <View style={styles.modalContainer}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Inserir Chave de Verificação</Text>
            <TextInput
              style={styles.verificationInput}
              placeholder="Digite a chave de verificação"
              placeholderTextColor="#999"
              value={verificationCode}
              onChangeText={setVerificationCode}
              keyboardType="numeric"
            />
            <TouchableOpacity style={styles.verifyButton} onPress={() => handleVerifyCode(selectedOrder.isRouteOrder)}>
              <Text style={styles.verifyButtonText}>Verificar</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.cancelButton} onPress={() => setVerificationModalVisible(false)}>
              <Text style={styles.cancelButtonText}>Cancelar</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      {/* MODAL CAPTCHA */}
      <Modal
        visible={captchaVisible}
        transparent
        animationType="fade"
        onRequestClose={cancelCaptcha}
      >
        <View style={styles.modalBackdrop}>
          <View style={styles.modalSlide}>
            <Text style={{ fontSize: 18, fontWeight: 'bold', marginBottom: 10 }}>
              Confirme que você é humano
            </Text>

            <View
              style={{
                width: 120, height: 80, alignSelf: 'center',
                justifyContent: 'center', alignItems: 'center',
                borderWidth: 1, borderColor: '#ccc', marginBottom: 10,
                transform: [{ rotate: `${(captchaDigit ?? 0) * 12 - 54}deg` }],
              }}
            >
              <Text style={{ fontSize: 52, fontWeight: '900', letterSpacing: 2 }}>
                {captchaDigit ?? ''}
              </Text>
            </View>

            <Text style={{ marginBottom: 6 }}>
              Digite o número acima (expira em {Math.max(0, Math.ceil(((captchaExpiresAt?.getTime() || 0) - Date.now())/1000))}s):
            </Text>

            <TextInput
              value={captchaInput}
              onChangeText={(t) => setCaptchaInput(t.replace(/[^0-9]/g, '').slice(0, 1))}
              keyboardType="number-pad"
              maxLength={1}
              style={{
                width: 80, height: 50, borderWidth: 1, borderColor: '#aaa',
                borderRadius: 8, textAlign: 'center', fontSize: 28, marginBottom: 14,
                backgroundColor: '#fff'
              }}
              autoFocus
            />

            <View style={{ flexDirection: 'row', flexWrap: 'wrap', width: 220, justifyContent: 'center', marginBottom: 12 }}>
              {[1,2,3,4,5,6,7,8,9,0].sort(() => Math.random()-0.5).map((n) => (
                <TouchableOpacity
                  key={n}
                  onPress={() => setCaptchaInput(String(n))}
                  style={{
                    width: 60, height: 44, borderWidth: 1, borderColor: '#ddd',
                    borderRadius: 8, margin: 5, alignItems: 'center', justifyContent: 'center',
                    backgroundColor: '#f7f7f7'
                  }}
                >
                  <Text style={{ fontSize: 18 }}>{n}</Text>
                </TouchableOpacity>
              ))}
            </View>

            <TouchableOpacity
              onPress={submitCaptchaAndAccept}
              style={{
                backgroundColor: '#00FF9D', paddingVertical: 12,
                paddingHorizontal: 20, borderRadius: 10, width: '100%', marginBottom: 8
              }}
            >
              <Text style={{ textAlign: 'center', color: '#203a43', fontWeight: 'bold' }}>OK</Text>
            </TouchableOpacity>

            <TouchableOpacity onPress={cancelCaptcha} style={{ paddingVertical: 10 }}>
              <Text style={{ textAlign: 'center', color: '#333' }}>Cancelar</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      <MenuModal
        visible={menuVisible}
        onClose={() => setMenuVisible(false)}
        setView={(newView) => {
          setView(newView);
          setMenuVisible(false);
        }}
        handleLogoff={handleLogoff}
        navigation={navigation}
      />
    </View>
  );
};

const renderTitle = (view) => {
  switch (view) {
    case 'acceptOrders': return 'Pedidos Pendentes';
    case 'accepted': return 'Pedidos Aceitos';
    case 'completed': return 'Histórico de Pedidos';
    case 'profile': return 'Perfil do Motoboy';
    default: return '';
  }
};

const OrderItem = ({
  item,
  navigation,
  onAccept,
  onCancel,
  onVerify,
  view,
  unreadMessagesMap,
}) => {
  return (
    <View style={styles.order}>
      <Text style={styles.orderText}>Pedido: {item.description}</Text>
      <Text style={styles.orderText}>Empresa: {item.companyName}</Text>
      <Text style={styles.orderText}>Endereço da Empresa: {item.companyAddress}</Text>
      <Text style={styles.orderText}>Telefone da Empresa: {item.companyPhone}</Text>

      {item.status !== 'completed' && (
        <>
          <Text style={styles.orderText}>Endereço de Entrega: {item.deliveryLocation}</Text>
          <Text style={styles.orderText}>Cliente: {item.clientName}</Text>
          <Text style={styles.orderText}>Telefone do Cliente: {item.clientPhone}</Text>
        </>
      )}

      <Text style={styles.orderText}>
        Valor do Pedido: {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(item?.orderValue ?? 0))}
      </Text>

      <Text style={styles.orderText}>Localização de Entrega: {item.locationType}: {item.locationValue}</Text>
      <Text style={styles.orderText}>Forma de Pagamento: {item.paymentMethod}</Text>
      {item.paymentMethod === 'cartao' && <Text style={styles.orderText}>Tipo de Cartão: {item.cardType}</Text>}
      {item.paymentMethod === 'dinheiro' && item.changeValue && (
        <Text style={styles.orderText}>Troco para: R$ {Number(item.changeValue).toFixed(2).replace('.', ',')}</Text>
      )}

      {view === 'accepted' && (
        <View style={styles.acceptedActions}>
          <TouchableOpacity style={styles.actionButton} onPress={() => onVerify(item)}>
            <Text style={styles.actionButtonText}>Verificar Código</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.button, styles.chatButton]}
            onPress={() =>
              navigation?.navigate('Chat', {
                orderId: item.id,
                companyId: item.companyId,
                motoboyId: item.motoboyId,
              })
            }
          >
            <Ionicons name="chatbubble-ellipses-outline" size={20} color="#FFF" />
            <Text style={styles.chatButtonText}>Chat</Text>

            {unreadMessagesMap?.[item.id] > 0 && (
              <View style={styles.notificationBadge}>
                <Text style={styles.notificationText}>1</Text>
              </View>
            )}
          </TouchableOpacity>
        </View>
      )}

      {item.status === 'pending' && (
        <TouchableOpacity
          style={styles.actionButton}
          onPress={() => onAccept?.()}
        >
          <Text style={styles.actionButtonText}>Aceitar Pedido</Text>
        </TouchableOpacity>
      )}

      {item.status === 'completed' && (
        <>
          <Text style={styles.orderText}>Motoboy: {item.motoboyName || 'Nome não disponível'}</Text>
          <Text style={styles.orderText}>Placa da Moto: {item.motoboyPlate || 'Placa não disponível'}</Text>
          <Text style={styles.orderText}>
            Concluído em: {item.completedAt ? new Date(item.completedAt.toDate()).toLocaleString() : 'Data não disponível'}
          </Text>
        </>
      )}
    </View>
  );
};

const MenuModal = ({ visible, onClose, setView, handleLogoff, navigation }) => (
  <Modal visible={visible} transparent={true} animationType="slide" onRequestClose={onClose}>
    <View style={styles.menuModal}>
      <MenuOption
        icon="assignment"
        text="Aceitar Pedidos"
        onPress={() => { setView('acceptOrders'); onClose(); }}
      />
      <MenuOption
        icon="check-circle"
        text="Pedidos Aceitos"
        onPress={() => { setView('accepted'); onClose(); }}
      />
      <MenuOption
        icon="history"
        text="Histórico"
        onPress={() => { setView('completed'); onClose(); }}
      />
      <MenuOption
        icon="person"
        text="Perfil"
        onPress={() => { setView('profile'); onClose(); }}
      />
      <MenuOption
        icon="bar-chart"
        text="Meus Ganhos"
        onPress={() => { navigation.navigate('MotoboyGainsScreen'); onClose(); }}
      />
      <Button title="Fechar Menu" onPress={onClose} color="#000" />
    </View>
  </Modal>
);

const MenuOption = ({ icon, text, onPress }) => (
  <TouchableOpacity style={styles.menuOption} onPress={onPress}>
    <MaterialIcons name={icon} size={24} color="#000" />
    <Text style={styles.menuOptionText}>{text}</Text>
  </TouchableOpacity>
);

const styles = StyleSheet.create({
  container: { flex: 1, padding: 16, backgroundColor: '#2c5364' },
  header: {},
  logoutButtonContainer: {
    position: 'absolute',
    top: 50, left: 10, zIndex: 10,
    paddingVertical: 20, paddingHorizontal: 20,
    backgroundColor: '#203a43', borderRadius: 50,
    alignItems: 'center', justifyContent: 'center',
    shadowColor: '#000', shadowOpacity: 0.3, shadowRadius: 10,
    shadowOffset: { width: 0, height: 5 }, elevation: 5,
  },
  menuButtonContainer: { position: 'absolute', top: 60, right: 20, zIndex: 10 },
  menuButton: { backgroundColor: '#000', padding: 15, borderRadius: 50, alignItems: 'center', justifyContent: 'center' },
  notificationBadge: {
    position: 'absolute', top: -5, right: -5,
    backgroundColor: 'red', borderRadius: 15, width: 20, height: 20,
    justifyContent: 'center', alignItems: 'center',
  },
  notificationText: { color: '#FFF', fontSize: 12, fontWeight: 'bold' },
  sectionTitle: { fontSize: 24, fontWeight: 'bold', color: '#FFF', textAlign: 'center', marginBottom: -70, marginTop: 100 },
  profileContainer: {
    flex: 1, padding: 16, backgroundColor: '#2c5364',
    borderRadius: 10, marginHorizontal: 10, marginTop: 40,
  },
  inputContainer: {
    flexDirection: 'row', alignItems: 'center', marginBottom: 16,
    borderColor: '#00FF9D', borderWidth: 1, borderRadius: 8, paddingHorizontal: 10,
    backgroundColor: '#203a43',
  },
  input: { flex: 1, height: 50, color: '#FFF', fontSize: 16, paddingLeft: 10, backgroundColor: '#2c5364' },
  saveButton: { backgroundColor: '#00FF9D', padding: 15, borderRadius: 10, alignItems: 'center', borderColor: '#FFF', borderWidth: 1 },
  saveButtonText: { color: '#FFF', fontSize: 16, fontWeight: 'bold' },
  editButton: { backgroundColor: '#000', padding: 15, borderRadius: 10, alignItems: 'center', borderColor: '#FFF', borderWidth: 1 },
  editButtonText: { color: '#FFF', fontSize: 16, fontWeight: 'bold' },
  menuModal: {
    flex: 1, justifyContent: 'center', alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
  },
  menuOption: {
    flexDirection: 'row', alignItems: 'center', backgroundColor: '#203a43',
    padding: 20, marginVertical: 10, borderRadius: 10, borderWidth: 1, borderColor: '#00FF9D',
    width: 250, justifyContent: 'space-between',
  },
  menuOptionText: { fontSize: 18, color: '#FFF' },
  order: {
    backgroundColor: '#203a43', padding: 16, marginBottom: 12, borderRadius: 10,
    shadowColor: '#00FF9D', shadowOpacity: 0.6, shadowRadius: 10,
    shadowOffset: { width: 0, height: 0 }, elevation: 5, borderWidth: 2, borderColor: '#00FF9D',
  },
  orderText: { fontSize: 16, color: '#FFF', marginBottom: 8 },
  pedidoContainer: { marginBottom: 16 },
  pedidoTitle: { fontSize: 18, fontWeight: 'bold', color: '#FFF', marginBottom: 8 },
  acceptedActions: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: 10 },
  actionsContainer: { flexDirection: 'row', justifyContent: 'space-around', marginTop: 10 },
  actionButton: { backgroundColor: '#00FF9D', padding: 10, borderRadius: 5, alignItems: 'center', justifyContent: 'center', marginHorizontal: 5 },
  actionButtonCancel: { flex: 1, backgroundColor: '#d9534f', padding: 10, borderRadius: 5, alignItems: 'center', justifyContent: 'center', marginHorizontal: 5 },
  actionButtonText: { fontSize: 16, fontWeight: 'bold', color: '#203a43' },
  modalContainer: { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: 'rgba(0, 0, 0, 0.8)' },
  modalContent: { backgroundColor: '#2c5364', padding: 20, borderRadius: 10, width: 300, alignItems: 'center' },
  modalTitle: { fontSize: 18, fontWeight: 'bold', color: '#FFF', marginBottom: 10 },
  verificationInput: {
    height: 70, color: '#FFF', fontSize: 24, paddingVertical: 10, paddingHorizontal: 15,
    backgroundColor: '#333', borderColor: '#555', borderWidth: 1, borderRadius: 8, marginBottom: 20, width: '100%', textAlign: 'center',
  },
  verifyButton: { backgroundColor: '#00FF9D', padding: 15, borderRadius: 10, alignItems: 'center', marginBottom: 10, width: '100%' },
  verifyButtonText: { color: '#203a43', fontSize: 16, fontWeight: 'bold' },
  cancelButton: { backgroundColor: '#FFF', padding: 15, borderRadius: 10, alignItems: 'center', width: '100%' },
  cancelButtonText: { color: '#203a43', fontSize: 16, fontWeight: 'bold' },
  supportButton: {
    position: 'absolute', bottom: 20, right: 20,
    backgroundColor: '#203a43', width: 60, height: 60, borderRadius: 30,
    alignItems: 'center', justifyContent: 'center',
    shadowColor: '#000', shadowOpacity: 0.3, shadowRadius: 10,
    shadowOffset: { width: 0, height: 5 }, elevation: 5,
  },
  supportButtonText: { color: '#FFF', fontSize: 16 },
  supportImage: { width: 40, height: 40, resizeMode: 'contain' },
  completedText: { color: '#28a745', fontSize: 16, fontWeight: 'bold', marginVertical: 10 },
  demandModalContent: {
    backgroundColor: '#203a43', padding: 20, borderRadius: 10, width: '90%', maxHeight: '80%',
    alignItems: 'center', shadowColor: '#000', shadowOpacity: 0.3, shadowRadius: 10,
    shadowOffset: { width: 0, height: 5 }, elevation: 5,
  },
  demandOrderCard: {
    backgroundColor: '#2c5364', borderWidth: 1, borderColor: '#00FF9D', borderRadius: 10, padding: 15, marginBottom: 10,
    shadowColor: '#00FF9D', shadowOpacity: 0.5, shadowRadius: 10, shadowOffset: { width: 0, height: 5 }, elevation: 5, width: '100%',
  },
  cardTitle: { fontSize: 18, fontWeight: 'bold', color: '#FFF', marginBottom: 8 },
  cardText: { fontSize: 16, color: '#FFF', marginBottom: 5 },
  cardLabel: { fontWeight: 'bold', color: '#00FF9D' },
  noOrdersText: { fontSize: 16, color: '#FFF', textAlign: 'center', marginTop: 20 },
  listContentContainer: { paddingBottom: 20 },
  demandTitle: { fontSize: 24, fontWeight: 'bold', color: '#FFF', marginBottom: 20, textAlign: 'center', backgroundColor: 'transparent' },
  statusButton: {
    width: 60, height: 60, borderRadius: 30, alignItems: 'center', justifyContent: 'center',
    position: 'absolute', bottom: 70, right: 5,
    shadowColor: '#00FF9D', shadowOpacity: 0.8, shadowRadius: 10, shadowOffset: { width: 0, height: 0 },
    elevation: 10, borderWidth: 2, borderColor: '#00FF9D',
    zIndex: 9999,
  },
  statusButtonText: { color: '#FFF', fontSize: 18, fontWeight: 'bold' },
  ordersContainer: { flex: 1, padding: 16, backgroundColor: '#2c5364' },
  offlineContainer: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: 16, backgroundColor: '#2c5364' },
  offlineText: { fontSize: 18, color: '#FFF', textAlign: 'center' },
  closeButton: { backgroundColor: '#00FF9D', paddingVertical: 12, paddingHorizontal: 20, borderRadius: 10, alignItems: 'center', marginTop: 20, width: '60%' },
  closeButtonText: { color: '#203a43', fontSize: 16, fontWeight: 'bold' },
  fixedAnimationContainer: {
    position: 'absolute', top: '60%', left: '50%', transform: [{ translateX: -50 }],
    width: 150, height: 150, justifyContent: 'center', alignItems: 'center', zIndex: 1,
  },
  fixedAnimation: { width: 200, height: 200 },
  searchingText: { marginTop: 10, fontSize: 16, color: '#FFF', fontWeight: 'bold', textAlign: 'center' },
  button: {},
  chatButton: {
    backgroundColor: '#007AFF', paddingVertical: 0, paddingHorizontal: 15, borderRadius: 5,
    alignItems: 'center', justifyContent: 'center', marginHorizontal: 5, width: 120,
  },
  chatButtonText: { fontSize: 16, fontWeight: 'bold', color: '#FFF' },
  modalBackdrop: { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'center', alignItems: 'center' },
  modalSlide: { backgroundColor: '#fff', borderRadius: 10, padding: 20, width: '90%', alignItems: 'center' },
  loadingContainer: { flex: 1, backgroundColor: '#2c5364', justifyContent: 'center', alignItems: 'center' },
});

export default MotoboyHomeScreen;
